import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { loginFlow, resendOtp, verifyOtp } from '../redux/slices/authSlice';
import { useParams } from "react-router-dom";
import { setQrCode } from "../redux/slices/qrSlice";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const ErrIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <circle cx="6.5" cy="6.5" r="6" stroke="#e05252" strokeWidth="1.3" />
    <path d="M6.5 3.5v3.5" stroke="#e05252" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="6.5" cy="9.5" r="0.7" fill="#e05252" />
  </svg>
);

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [otpError, setOtpError] = useState("");

  // Read loading AND isNewUser from Redux
  const { loading, isNewUser } = useSelector((state) => state.auth);

  const { code } = useParams();
  const { t } = useTranslation();

  const [step, setStep] = useState('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const existingCode = useSelector((state) => state.qr.code);
  const savedCode = localStorage.getItem("qrCode");

  useEffect(() => {
    if (!existingCode && savedCode) {
      dispatch(setQrCode(savedCode));
    }
  }, []);

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();

    if (phoneNumber.length !== 10) {
      toast({
        title: t("errors.invalidMobileTitle"),
        description: t("errors.invalidMobileDesc"),
        variant: "destructive",
      });
      return;
    }

    const res = await dispatch(loginFlow(phoneNumber));

    if (res.meta.requestStatus === 'fulfilled') {
      setStep("otp");
    } else {
      toast({
        title: t("errors.genericTitle"),
        description:
          res.payload === "Customer not found"
            ? t("errors.customerNotFound")
            : t("errors.genericDesc"),
        variant: "destructive",
      });
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError("");
    if (value && index < 3) document.getElementById(`otp-${index + 1}`)?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    // Support both form onSubmit and direct button click
    if (e && e.preventDefault) e.preventDefault();

    const finalOtp = otp.join('');
    if (finalOtp.length !== 4) {
      setOtpError(t("errors.otpRequired") || "Please enter the 4-digit OTP");
      return;
    }

    const res = await dispatch(verifyOtp({ otp: finalOtp, mobile: phoneNumber }));

    if (res.meta.requestStatus === 'fulfilled') {
      // isNewUser is now in the Redux state (updated by verifyOtp.fulfilled)
      // Also check payload directly in case state hasn't updated yet
      const newUser = res.payload?.isNewUser ?? isNewUser;
      navigate(newUser ? '/profile-setup' : '/home');
    } else {
      setOtpError(t("errors.invalidOtp") || "Invalid OTP. Please try again.");
    }
  };

const handleResendOtp = async () => {
  const res = await dispatch(resendOtp(phoneNumber));

  if (res.meta.requestStatus === 'fulfilled') {
    toast({ title: t("success.otpResent") });
  } else {
    toast({
      title: t("errors.genericTitle"),
      description: t("errors.genericDesc"),
      variant: "destructive",
    });
  }
};

  return (
    <div className="min-h-screen bg-white md:bg-[#F5F6FA] md:flex md:justify-center md:items-start">

      <div className="w-full max-w-md mx-auto md:bg-white md:rounded-3xl md:shadow-lg md:px-6 md:py-8 md:h-screen flex flex-col">

        {/* Logo */}
        {step === 'phone' && (
          <div className="text-center mt-6 mb-4 leading-tight w-50 mx-auto">
            <h1 className="text-2xl font-bold text-gray-800">
              {t("app.title")}
            </h1>
            <p className="text-xl font-semibold mt-2 mb-5 text-gray-800">
              {t("app.subtitle")}
            </p>
          </div>
        )}

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto px-4 pb-6 mt-6 md:mt-0">

          {step === 'otp' && (
            <div className="flex gap-3 mb-6 mt-2">
              <button onClick={() => setStep('phone')}>
                <ArrowLeft size={28} style={{ color: '#1A1F36' }} />
              </button>
              <h2 className="text-xl font-bold text-center w-full mr-4" style={{ color: '#1A1F36' }}>
                {t("login.loginWithOtp")}
              </h2>
            </div>
          )}

          {step === 'phone' ? (
            <form onSubmit={handlePhoneSubmit}>
              <h1 className="text-2xl font-bold text-center mb-3" style={{ color: '#1A1F36' }}>
                {t("common.login")}
              </h1>
              <div className="mb-5">
                <p className="text-gray-600 font-semibold">{t("common.mobileNumber")}</p>

                <div className="flex items-center gap-2 rounded-lg px-3 py-3 mt-2 border-2 border-[#1A1F36]">
                  <span className="font-semibold text-[#1A1F36]">+91</span>
                  <div className="h-5 w-px bg-gray-300"></div>

                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) =>
                      setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))
                    }
                    className="flex-1 bg-transparent outline-none text-base text-[#1A1F36]"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || phoneNumber.length !== 10}
                className="w-full text-white py-4 text-sm rounded-xl shadow-md"
                style={{ backgroundColor: '#1A1F36' }}
              >
                {loading ? t("login.sendingOtp") : t("common.continue")}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit}>
              <p className="text-center text-gray-500 text-sm mb-2 leading-snug">
                {t("login.enterOtp")} {phoneNumber}
              </p>

              <div className="flex justify-center gap-3 mb-6 mt-6">
                {otp.map((v, i) => (
                  <input
                    key={i}
                    id={`otp-${i}`}
                    type="text"
                    maxLength={1}
                    inputMode="numeric"
                    value={v}
                    onChange={(e) =>
                      handleOtpChange(i, e.target.value.replace(/\D/g, ''))
                    }
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className={`w-12 h-12 rounded-xl border text-center text-lg font-bold outline-none ${
                      otpError
                        ? "border-red-400 bg-red-50"
                        : "bg-gray-50 border-[#1A1F36]"
                    }`}
                  />
                ))}
              </div>

              {otpError && (
                <p className="text-center text-red-500 text-xs mb-3 flex items-center justify-center gap-1">
                  <ErrIcon />
                  {otpError}
                </p>
              )}

              <Button
                type="submit"
                disabled={loading || otp.join('').length !== 4}
                className="w-full text-white py-4 text-sm rounded-xl shadow-md"
                style={{ backgroundColor: '#1A1F36' }}
              >
                {loading ? t("common.verifying") : t("common.verifyContinue")}
              </Button>

              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  className="font-semibold text-sm text-[#1A1F36]"
                >
                  {t("login.resendOtp")}
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default Login;